/**
 * soakTelemetry.js ✦ iter207
 * ─────────────────────────────────────────────────────────────
 * Lifecycle ring buffer + counters + canonical
 * `eventTimelineSignature` for the soak test. Lives as a tiny
 * module-level singleton so any pipeline can `emit()` without
 * importing UI state.
 *
 * Per Craig (iter207 directive): "later you'll discover certain
 * bugs only happen in specific lifecycle orderings. That tiny
 * addition will become extremely valuable."
 *
 *   The ring buffer captures the rhythm:
 *       [
 *         { t: 1234, evt: 'cam:start',       profile: 'guestbook' },
 *         { t: 1456, evt: 'mic:start',       profile: 'voice_message' },
 *         { t: 2001, evt: 'cam:rec-start' },
 *         { t: 7001, evt: 'cam:rec-stop',    bytes: 2347612 },
 *         { t: 7080, evt: 'upload:begin' },
 *         { t: 9421, evt: 'upload:end' },
 *         ...
 *       ]
 *
 * Counters track cumulative stress signatures (cycle count,
 * stream restarts, recorder pairs, total bytes, interruption
 * counts, devicechange counts, frame drops).
 *
 * Memory deltas sample `performance.memory.usedJSHeapSize` where
 * available (Chrome). Safari intentionally hides this — we
 * gracefully absent it.
 *
 * Capture conditions (visibilityState, thermal, battery saver)
 * are sampled at subscriber poll time. The pipelines never have
 * to know about them.
 *
 * Subscriber model is intentionally simple: one global emitter,
 * one subscribe() callback per consumer. The soak page uses it;
 * production builds tree-shake nothing (the module is always
 * imported) but the runtime overhead is a single ring write per
 * emit, gated by ENABLED.
 */

const RING_LIMIT = 240;            // ~last 4 minutes at 60 events/min
const MEMORY_SAMPLE_LIMIT = 240;

/**
 * KNOWN_EVENTS — canonical commitment to lifecycle vocabulary.
 *
 * Bumping this list is intentional: every entry is something the
 * post-soak analysis ASSUMES will be in the eventTimelineSignature
 * when looking for orderings. Adding a new evt means committing
 * to its meaning across pipelines + reports.
 */
export const KNOWN_EVENTS = Object.freeze([
  // Camera pipeline
  'cam:start', 'cam:stop', 'cam:restart',
  'cam:rec-start', 'cam:rec-stop',
  'cam:track-ended', 'cam:devicechange',
  // Audio pipeline
  'mic:start', 'mic:stop', 'mic:restart',
  'mic:rec-start', 'mic:rec-stop',
  'mic:track-ended', 'mic:devicechange', 'mic:interrupt',
  // Document lifecycle
  'doc:hidden', 'doc:visible',
  // Upload rhythm
  'upload:begin', 'upload:end', 'upload:fail',
  // Soak page bookkeeping
  'soak:enable', 'soak:disable', 'soak:cycle',
]);

const state = {
  enabled: false,
  startedAt: 0,
  ring: [],                        // eventTimelineSignature
  memorySamples: [],               // { t, used, total, limit }
  counters: {
    cycles: 0,
    streamStartCamera: 0,
    streamStartMic: 0,
    streamRestartsCamera: 0,
    streamRestartsMic: 0,
    recorderPairsCamera: 0,
    recorderPairsMic: 0,
    trackEndedCamera: 0,
    trackEndedMic: 0,
    deviceChangesCamera: 0,
    deviceChangesMic: 0,
    interruptionsMic: 0,
    visibilityHides: 0,
    uploadBegan: 0,
    uploadCompleted: 0,
    uploadFailed: 0,
    totalBytes: 0,
  },
  subscribers: new Set(),
};

let memoryRaf = 0;

function notify() {
  for (const fn of state.subscribers) {
    try { fn(); } catch { /* subscriber crashes never affect the ring */ }
  }
}

/**
 * Emit a single lifecycle event. Always safe (no-op when soak
 * telemetry is not enabled).
 *
 *   emit('cam:start', { profile: 'guestbook' });
 *   emit('cam:rec-stop', { bytes: 2347612, mime: 'video/webm' });
 *
 * `evt` is a canonical string. Reserved namespaces:
 *   cam:*    — useBoothCameraPipeline
 *   mic:*    — useParticipationAudioPipeline
 *   rec:*    — composite recorders (e.g. GuestbookRecorder)
 *   upload:* — surface-layer uploads
 *   doc:*    — document/visibility lifecycle
 *   soak:*   — the soak page itself (cycle bookkeeping)
 */
export function emit(evt, meta = undefined) {
  if (!state.enabled) return;
  const entry = { t: Math.round(performance.now() - state.startedAt), evt };
  if (meta !== undefined) entry.meta = meta;
  state.ring.push(entry);
  if (state.ring.length > RING_LIMIT) state.ring.splice(0, state.ring.length - RING_LIMIT);

  // Auto-counter mapping — keeps counters honest without surfaces
  // needing to bump them by name.
  const c = state.counters;
  switch (evt) {
    case 'cam:start':            c.streamStartCamera += 1; break;
    case 'cam:restart':          c.streamRestartsCamera += 1; break;
    case 'cam:rec-start':        /* paired on rec-stop */ break;
    case 'cam:rec-stop':         c.recorderPairsCamera += 1;
                                 if (meta?.bytes) c.totalBytes += meta.bytes;
                                 break;
    case 'cam:track-ended':      c.trackEndedCamera += 1; break;
    case 'cam:devicechange':     c.deviceChangesCamera += 1; break;
    case 'mic:start':            c.streamStartMic += 1; break;
    case 'mic:restart':          c.streamRestartsMic += 1; break;
    case 'mic:rec-stop':         c.recorderPairsMic += 1;
                                 if (meta?.bytes) c.totalBytes += meta.bytes;
                                 break;
    case 'mic:track-ended':      c.trackEndedMic += 1; break;
    case 'mic:devicechange':     c.deviceChangesMic += 1; break;
    case 'mic:interrupt':        c.interruptionsMic += 1; break;
    case 'doc:hidden':           c.visibilityHides += 1; break;
    case 'upload:begin':         c.uploadBegan += 1; break;
    case 'upload:end':           c.uploadCompleted += 1;
                                 if (meta?.bytes) c.totalBytes += meta.bytes;
                                 break;
    case 'upload:fail':          c.uploadFailed += 1; break;
    case 'soak:cycle':           c.cycles += 1; break;
    default: /* free-form event — only ring buffer updates */
  }
  notify();
}

export function enable() {
  if (state.enabled) return;
  state.enabled = true;
  state.startedAt = performance.now();
  state.ring = [];
  state.memorySamples = [];
  state.counters = {
    cycles: 0,
    streamStartCamera: 0,
    streamStartMic: 0,
    streamRestartsCamera: 0,
    streamRestartsMic: 0,
    recorderPairsCamera: 0,
    recorderPairsMic: 0,
    trackEndedCamera: 0,
    trackEndedMic: 0,
    deviceChangesCamera: 0,
    deviceChangesMic: 0,
    interruptionsMic: 0,
    visibilityHides: 0,
    uploadBegan: 0,
    uploadCompleted: 0,
    uploadFailed: 0,
    totalBytes: 0,
  };
  emit('soak:enable');
  // Memory sampler — every 5s, lightweight.
  const sample = () => {
    if (!state.enabled) return;
    if (typeof performance !== 'undefined' && performance.memory) {
      state.memorySamples.push({
        t: Math.round(performance.now() - state.startedAt),
        used: performance.memory.usedJSHeapSize,
        total: performance.memory.totalJSHeapSize,
        limit: performance.memory.jsHeapSizeLimit,
      });
      if (state.memorySamples.length > MEMORY_SAMPLE_LIMIT) {
        state.memorySamples.splice(0, state.memorySamples.length - MEMORY_SAMPLE_LIMIT);
      }
      notify();
    }
    memoryRaf = setTimeout(sample, 5000);
  };
  sample();
  // visibilitychange auto-emit so the timeline reflects backgrounding.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }
}

export function disable() {
  if (!state.enabled) return;
  emit('soak:disable');
  state.enabled = false;
  if (memoryRaf) { clearTimeout(memoryRaf); memoryRaf = 0; }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibility);
  }
}

function onVisibility() {
  if (document.hidden) emit('doc:hidden');
  else emit('doc:visible');
}

export function subscribe(fn) {
  state.subscribers.add(fn);
  return () => state.subscribers.delete(fn);
}

export function snapshot() {
  return {
    enabled: state.enabled,
    startedAt: state.startedAt,
    ring: state.ring.slice(),
    memorySamples: state.memorySamples.slice(),
    counters: { ...state.counters },
  };
}

/**
 * eventTimelineSignature — the canonical, compact chronological
 * array a soak report ships back to the team. Strips meta down
 * to the rhythm fields that matter for "did this happen in the
 * expected order".
 */
export function eventTimelineSignature() {
  return state.ring.map((e) => {
    const sig = { t: e.t, evt: e.evt };
    if (e.meta?.profile) sig.profile = e.meta.profile;
    if (e.meta?.mime) sig.mime = e.meta.mime;
    if (e.meta?.bytes) sig.bytes = e.meta.bytes;
    return sig;
  });
}

/**
 * captureConditions — environmental state sampled at report
 * time. Optional: not every browser exposes battery / thermal,
 * so the fields gracefully absent.
 */
export async function captureConditions() {
  const out = {
    visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    deviceMemoryGB: typeof navigator !== 'undefined' ? navigator.deviceMemory : undefined,
    hardwareConcurrency: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
    onLine: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
  };
  // Battery API (Chrome on Android, sometimes desktop).
  try {
    if (typeof navigator !== 'undefined' && navigator.getBattery) {
      const b = await navigator.getBattery();
      out.batteryLevel = b.level;
      out.batteryCharging = b.charging;
      // batterySaver is not directly exposed; chrome surfaces it
      // via the "Save-Data" header (not a JS API). We capture the
      // hint from connection.saveData where available.
      if (navigator.connection) {
        out.saveData = !!navigator.connection.saveData;
        out.effectiveType = navigator.connection.effectiveType;
      }
    } else if (typeof navigator !== 'undefined' && navigator.connection) {
      out.saveData = !!navigator.connection.saveData;
      out.effectiveType = navigator.connection.effectiveType;
    }
  } catch { /* ignore — best-effort signal only */ }
  // Thermal API — chrome behind a flag, optional.
  try {
    if (typeof navigator !== 'undefined' && 'thermal' in navigator) {
      out.thermalState = navigator.thermal?.state;
    }
  } catch { /* ignore */ }
  return out;
}

/**
 * Build a complete soak report bundle for export.
 */
export async function exportReport({ note } = {}) {
  return {
    schema: 'eventdrop.soak.report.v1',
    generatedAt: new Date().toISOString(),
    note: note || '',
    durationMs: state.startedAt ? Math.round(performance.now() - state.startedAt) : 0,
    counters: { ...state.counters },
    eventTimelineSignature: eventTimelineSignature(),
    memorySamples: state.memorySamples.slice(),
    conditions: await captureConditions(),
  };
}

export default {
  enable,
  disable,
  emit,
  subscribe,
  snapshot,
  eventTimelineSignature,
  captureConditions,
  exportReport,
};
